import { useCallback, useEffect, useRef, useState } from 'react';
import { axios as apiService } from '@service';
import { BASE_URL } from '@service/apis';
import useProjectId from './useProjectId';
import { throttle } from 'lodash';

const serviceUrl = process.env.REACT_APP_service_url;

function useConcurrentEditLock({
  retryTimes = -1,
  // retryDelay = 100,
  interval = 30 * 1000,
  auto = true,
  onPollingFail,
} = {}) {
  const [lockData, setLockData] = useState(null);
  const [loading, setLoading] = useState(false);
  const projectId = useProjectId();
  const failTimes = useRef(0);

  const timerId = useRef(null);

  const handlePolling = async (polling = true) => {
    const res = await sendQuery();
    if (res) {
      failTimes.current = 0;
      setLockData(res);
    } else {
      failTimes.current += 1;
    }
    if (polling) {
      if (failTimes.current >= retryTimes && retryTimes >= 0) {
        typeof onPollingFail === 'function' && onPollingFail();
      } else {
        timerId.current = setTimeout(handlePolling, interval);
      }
    }
    return res;
  };

  const sendQuery = async () => {
    setLoading(true);
    try {
      const res = await apiService.get(
        `${BASE_URL}/project/lockModule/getLockInfo/${projectId}`,
      );
      setLoading(false);
      return res;
    } catch (e) {
      console.log(e);
    }
    setLoading(false);
    return null;
  };

  const releaseLock = useCallback(
    ({ moduleIdentifier, userEmail, userName }) =>
      apiService.post(`${BASE_URL}/project/lockModule/unlock/${projectId}`, {
        moduleIdentifier,
        userEmail,
        userName,
      }),
    [projectId],
  );

  const releaseLockBeforeUnload = useCallback(
    ({ moduleIdentifier, userEmail, userName }) => {
      const body = {
        moduleIdentifier,
        userEmail,
        userName,
      };
      const headers = {
        type: 'application/json',
        authorization: window.localStorage.getItem('accessToken'),
        Accept: 'application/json, text/plain, */*',
        'auth-origin': ' http://localhost:3000',
        'Content-Type': 'application/json',
      };
      fetch(`${serviceUrl}api/v1/project/lockModule/unlock/${projectId}`, {
        method: 'POST',
        body: JSON.stringify(body),
        mode: 'cors',
        keepalive: true, // allow the request to outlive the page
        headers,
      });
    },
    [projectId],
  );

  const setLock = useCallback(
    ({ moduleIdentifier, userEmail, userName }) => {
      setLoading(true);
      return apiService
        .post(`${BASE_URL}/project/lockModule/lock/${projectId}`, {
          moduleIdentifier,
          userEmail,
          userName,
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [projectId],
  );

  const extendValidity = useCallback(
    ({ moduleIdentifier, userEmail }, config = {}) =>
      apiService.post(
        `${BASE_URL}/project/lockModule/extendValidity/${projectId}`,
        {
          moduleIdentifier,
          userEmail,
        },
        config,
      ),
    [projectId],
  );

  useEffect(() => {
    if (auto) {
      handlePolling();
    }
    return () => {
      clearInterval(timerId.current);
    };
  }, [projectId]);

  return {
    lockData,
    releaseLock,
    releaseLockBeforeUnload,
    setLock,
    extendValidity: throttle(extendValidity, 600),
    startPolling: handlePolling,
    handlePolling,
    sendQuery,
    loading,
  };
}

export default useConcurrentEditLock;
